import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Megaphone, Eye } from "lucide-react";
import { API, authHeaders } from "./shared";
import BlastPreviewModal from "./BlastPreviewModal";
import BestSendTimeHint from "./BestSendTimeHint";

const ClientBlastModal = ({ onClose }) => {
  const [subject, setSubject] = useState("New Arrivals at Afro Magic ✨");
  const [campaignName, setCampaignName] = useState("");
  const [message, setMessage] = useState("Hi {name},\n\nWe just added new hair & beauty products to our shop! Take a look at what's fresh — perfect for keeping your braids and natural hair flawless.");
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const inputCls = "w-full bg-brand-bg border border-brand-border text-brand-text font-sans text-sm px-3 py-2 focus:border-brand-gold outline-none";

  const openPreview = () => {
    if (!subject.trim() || !message.trim()) { toast.error("Subject and message are required"); return; }
    setShowPreview(true);
  };

  const send = async () => {
    if (!window.confirm("Send this product announcement to your ENTIRE email list?")) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/shop/blast`, { subject, message, name: campaignName || null }, authHeaders());
      toast.success(`Blast sent to ${r.data.sent} of ${r.data.total} subscribers`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send blast");
      setShowPreview(false);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="client-blast-modal">
      <div className="bg-brand-surface border border-brand-gold/50 w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg text-brand-text flex items-center gap-2">
            <Megaphone size={16} className="text-brand-gold" /> Client Blast — New Products
          </h3>
          <button data-testid="blast-close-btn" onClick={onClose} className="p-1.5 text-brand-text-muted hover:text-brand-text"><X size={16} /></button>
        </div>
        <p className="font-sans text-xs text-brand-text-muted mb-4">
          Emails your full subscriber list featuring your 3 newest approved products, with a "Shop Hair &amp; Beauty" button. Use {"{name}"} to personalize.
        </p>
        <BestSendTimeHint />
        <label className="font-sans text-xs text-brand-text-muted">Campaign name (optional — for your history)</label>
        <input data-testid="blast-campaign-name-input" className={`${inputCls} mb-3`} value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="e.g. June New Arrivals" />
        <label className="font-sans text-xs text-brand-text-muted">Subject</label>
        <input data-testid="blast-subject-input" className={`${inputCls} mb-3`} value={subject} onChange={(e) => setSubject(e.target.value)} />
        <label className="font-sans text-xs text-brand-text-muted">Message</label>
        <textarea data-testid="blast-message-input" rows={5} className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} />
        <div className="flex gap-3 mt-4">
          <button data-testid="blast-preview-btn" onClick={openPreview} className="flex items-center gap-2 px-5 py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors">
            <Eye size={13} /> Preview Email
          </button>
          <button data-testid="blast-cancel-btn" onClick={onClose} className="px-5 py-2 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold transition-colors">Cancel</button>
        </div>
      </div>
      {showPreview && (
        <BlastPreviewModal
          subject={subject}
          message={message}
          campaignName={campaignName}
          approving={sending}
          onApprove={send}
          onEdit={() => setShowPreview(false)}
          onClose={() => setShowPreview(false)}
          onScheduled={onClose}
        />
      )}
    </div>
  );
};

export default ClientBlastModal;
